import { createLogger } from "./logger"
const logger = createLogger("dateFormatting")

/**
 * Date Formatting Utilities
 * 
 * Provides consistent date formatting across the application.
 * All functions handle invalid dates gracefully and return fallback values.
 */

/**
 * Format a date as a localized date string
 * @param {string|Date} date - The date to format
 * @param {string} [locale='en-US'] - The locale to use
 * @param {Intl.DateTimeFormatOptions} [options] - Custom formatting options
 * @returns {string} Formatted date string or fallback
 */
export function formatDate(date, locale = 'en-US', options = {}) {
  if (!date) return '-';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };
    
    return dateObj.toLocaleDateString(locale, defaultOptions);
  } catch (error) {
    logger.error('Error formatting date', error);
    return '-';
  }
}

/**
 * Format a date with time as a localized string
 * @param {string|Date} date - The date to format
 * @param {string} [locale='en-US'] - The locale to use
 * @param {Intl.DateTimeFormatOptions} [options] - Custom formatting options
 * @returns {string} Formatted datetime string or fallback
 */
export function formatDateTime(date, locale = 'en-US', options = {}) {
  if (!date) return '-';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };
    
    return dateObj.toLocaleString(locale, defaultOptions);
  } catch (error) {
    logger.error('Error formatting datetime', error);
    return '-';
  }
}

/**
 * Format a date as "X days/hours/minutes ago"
 * @param {string|Date} date - The date to format
 * @returns {string} Relative time string
 */
export function formatRelativeTime(date) {
  if (!date) return 'Unknown';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 'Unknown';
    
    const now = new Date();
    const diffMs = now - dateObj;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);
    
    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    if (diffWeek < 4) return `${diffWeek} week${diffWeek !== 1 ? 's' : ''} ago`;
    if (diffMonth < 12) return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
    return `${diffYear} year${diffYear !== 1 ? 's' : ''} ago`;
  } catch (error) {
    logger.error('Error formatting relative time', error);
    return 'Unknown';
  }
}

/**
 * Format a date in short format (just date, no year if current year)
 * @param {string|Date} date - The date to format
 * @param {string} [locale='en-US'] - The locale to use
 * @returns {string} Short date string
 */
export function formatShortDate(date, locale = 'en-US') {
  if (!date) return '-';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    
    const now = new Date();
    const isCurrentYear = dateObj.getFullYear() === now.getFullYear();
    
    const options = {
      month: 'short',
      day: 'numeric',
      ...(isCurrentYear ? {} : { year: 'numeric' })
    };
    
    return dateObj.toLocaleDateString(locale, options);
  } catch (error) {
    logger.error('Error formatting short date', error);
    return '-';
  }
}

/**
 * Format a date for display with time if today, otherwise just date
 * @param {string|Date} date - The date to format
 * @param {string} [locale='en-US'] - The locale to use
 * @returns {string} Smart formatted date
 */
export function formatSmartDateTime(date, locale = 'en-US') {
  if (!date) return '-';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    
    const now = new Date();
    const isToday = dateObj.toDateString() === now.toDateString();
    
    if (isToday) {
      return dateObj.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    return formatShortDate(date, locale);
  } catch (error) {
    logger.error('Error formatting smart datetime', error);
    return '-';
  }
}

/**
 * Check if a date is valid
 * @param {string|Date} date - The date to check
 * @returns {boolean} Whether the date is valid
 */
export function isValidDate(date) {
  if (!date) return false;
  const dateObj = date instanceof Date ? date : new Date(date);
  return !isNaN(dateObj.getTime());
}

/**
 * Parse a date string safely
 * @param {string} dateString - The date string to parse
 * @returns {Date|null} Parsed date or null if invalid
 */
export function parseDate(dateString) {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Format a date for ISO string (for API requests)
 * @param {string|Date} date - The date to format
 * @returns {string|null} ISO string or null
 */
export function toISOString(date) {
  if (!date) return null;
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    return isNaN(dateObj.getTime()) ? null : dateObj.toISOString();
  } catch {
    return null;
  }
}

// Pre-configured formatters for common use cases
export const dateFormatters = {
  // Standard date: "Jan 15, 2024"
  standard: (date) => formatDate(date),
  
  // Full datetime: "Jan 15, 2024, 2:30 PM"
  full: (date) => formatDateTime(date),
  
  // Short date: "Jan 15" (no year if current year)
  short: (date) => formatShortDate(date),
  
  // Relative: "2 hours ago"
  relative: (date) => formatRelativeTime(date),
  
  // Smart: Shows time if today, otherwise shows date
  smart: (date) => formatSmartDateTime(date),
  
  // For admin tables
  adminDate: (date) => formatDate(date, 'en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  }),
  
  // For timestamps with full info
  timestamp: (date) => formatDateTime(date, 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
};

export default {
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatShortDate,
  formatSmartDateTime,
  isValidDate,
  parseDate,
  toISOString,
  dateFormatters
};
